import { Link } from "react-router-dom";
import {
    Globe2, Phone, ArrowRight, CheckCircle2, Star, Award, Users, Clock,
    Video, GraduationCap, ShieldCheck, MessageCircle, BadgeDollarSign,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import AEOSchema from "../components/AEOSchema";
import Breadcrumbs from "../components/Breadcrumbs";
import { COUNTRIES_DATA } from "../data/countriesData";
import { SOCIAL } from "../data/siteData";
import { getInternationalHreflangs } from "../lib/hreflang";

const ENQUIRY_PHONE = "+91 70005 30316";
const ENQUIRY_PHONE_RAW = "+917000530316";

const CANONICAL = "https://anandtutorials.com/online-tuition-worldwide";
const SEO_TITLE = "Best 1:1 Live Online Tuition Worldwide — The Best One on One Online Classes in the World | Anand Tutorials";
const SEO_DESCRIPTION = "The best one on one online classes in the world — 1:1 live online tuition from India for students in 27+ countries. Cambridge, Oxford, IIT & AIIMS educated tutors for IB, IGCSE, AP, A-Level, Common Core, GCSE, VCE, HSC, CBSE & ICSE. Time-zone friendly, pay in your currency. Free demo. WhatsApp +91 70005 30316.";
const SEO_KEYWORDS = "best one on one online classes in the world, best online tutoring platform, best online tutoring, online tuition worldwide, global online tutors from India, international online tuition, NRI online tutors, IB tutor online, IGCSE tutor online, A-Level tutor online, AP tutor online, CBSE tutor for NRI, ICSE tutor for NRI, online tuition USA, online tuition UK, online tuition Australia, online tuition Canada, online tuition UAE, online tuition Singapore, online tuition Germany, online tuition Ireland, online tuition Malaysia, online tuition Japan";

// Group countries into regional buckets for scannable UX + strong internal linking
const REGIONS = [
    {
        title: "North America",
        icon: "🌎",
        countries: ["usa", "canada"],
    },
    {
        title: "United Kingdom & Ireland",
        icon: "🇬🇧",
        countries: ["uk", "ireland"],
    },
    {
        title: "Europe",
        icon: "🇪🇺",
        countries: ["germany", "france", "netherlands", "switzerland", "belgium", "italy", "spain", "norway", "sweden", "finland", "denmark"],
    },
    {
        title: "Middle East / GCC",
        icon: "🌍",
        countries: ["uae", "saudi-arabia", "qatar", "kuwait", "oman", "bahrain"],
    },
    {
        title: "Asia-Pacific",
        icon: "🌏",
        countries: ["singapore", "malaysia", "japan", "south-korea", "australia", "new-zealand"],
    },
];

export default function OnlineTuitionWorldwide() {
    const allCountries = Object.values(COUNTRIES_DATA);
    const countryCount = allCountries.length;

    // Aggregate rating stats
    const totalReviews = allCountries.reduce(
        (sum, c) => sum + parseInt(String(c.rating.count).replace(/,/g, ""), 10),
        0
    );

    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": `${CANONICAL}#organization`,
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "foundingDate": "2013",
        "description": "Anand Tutorials is India's trusted 1:1 live online tutoring platform, serving students in 27+ countries with Cambridge / IIT / AIIMS educated mentors across every major curriculum.",
        "email": "anandhometutorials@gmail.com",
        "telephone": ENQUIRY_PHONE,
        "areaServed": allCountries.map((c) => ({ "@type": "Country", "name": c.fullName })),
        "sameAs": [SOCIAL.facebook, SOCIAL.instagram].filter(Boolean),
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": String(totalReviews),
            "bestRating": "5",
            "worstRating": "1",
        },
    };

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${CANONICAL}#service`,
        "serviceType": "1:1 Live Online Tuition",
        "provider": { "@id": `${CANONICAL}#organization` },
        "areaServed": allCountries.map((c) => ({ "@type": "Country", "name": c.fullName })),
        "audience": { "@type": "PeopleAudience", "audienceType": "Students Grade 1-12 + NEET / JEE / SAT aspirants worldwide" },
        "category": "Online Educational Services",
        "url": CANONICAL,
        "description": "Global 1:1 live online tuition from India — IB, IGCSE, A-Level, AP, Common Core, GCSE, VCE, HSC, CBSE, ICSE and competitive exams.",
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${CANONICAL}#webpage`,
        "url": CANONICAL,
        "name": SEO_TITLE,
        "description": SEO_DESCRIPTION,
        "inLanguage": "en",
        "isPartOf": { "@id": `${CANONICAL}#organization` },
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".speakable-headline", ".speakable-intro"],
        },
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${CANONICAL}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "1:1 Online Classes", "item": "https://anandtutorials.com/online-classes" },
            { "@type": "ListItem", "position": 3, "name": "Worldwide", "item": CANONICAL },
        ],
    };

    const faqs = [
        ["Which countries do you serve for 1:1 live online tuition?", `Anand Tutorials serves students in ${countryCount}+ countries — including USA, UK, Canada, Australia, New Zealand, UAE, Saudi Arabia, Qatar, Kuwait, Oman, Bahrain, Singapore, Malaysia, Japan, South Korea, Germany, France, Netherlands, Ireland, Switzerland, Belgium, Italy, Spain, Norway, Sweden, Finland and Denmark. If your country isn't listed, WhatsApp our Global Team — we very likely already teach there.`],
        ["Which curricula do you cover for international students?", "IB MYP & DP (HL/SL), IGCSE Cambridge, AS/A-Level (Edexcel, CIE, AQA), AP (US), Common Core, GCSE, VCE (Australia), HSC (Australia), Leaving Certificate (Ireland), NCEA (New Zealand), Matura (Switzerland), Abitur subject support (Germany), SPM (Malaysia) — plus full CBSE and ICSE for Indian-origin NRI students."],
        ["How do you handle different time zones?", "Every country has a dedicated tutor pool that specifically runs shifts aligned to that country's after-school hours. USA/Canada = late-evening/overnight IST; UK/Europe/Middle East = evening IST; Asia-Pacific/Australia/NZ = mid-day IST. We match your child to a tutor whose availability fits your slot, not the reverse."],
        ["Can I pay in my local currency?", "Yes — invoices are issued in your country's currency (USD, GBP, EUR, AUD, CAD, SGD, AED, SAR, JPY, KRW, MYR, NOK, SEK, DKK, CHF, QAR, KWD, OMR, BHD etc). Pay via Wise, Revolut, PayPal, Stripe or international bank transfer. No conversion penalties."],
        ["Are the tutors truly qualified?", "Yes — every internationally-facing tutor is hand-picked from Cambridge, Oxford, IIT, AIIMS and Ivy League alumni pools. Less than 12% of applicants make it into our global network. Every tutor is interviewed, demo-tested and background-verified before assignment."],
        ["Can my NRI child continue Indian curriculum (CBSE / ICSE) while abroad?", "Absolutely. Our NRI program supports Class 1-12 CBSE and ICSE anywhere in the world — perfectly aligned with school back home, including Hindi as a second language."],
        ["Is the first demo class really free?", "Yes — 100% free, full-length demo class, no advance payment, no obligation. Parents may attend the entire demo. WhatsApp our Global Team to book."],
        ["Do you cover college entrance exams like SAT, ACT, JEE and NEET?", "Yes — SAT, ACT, PSAT, AMC, AIME, USACO for US-bound students; UCAS/MAT/STEP/BMAT/UCAT for UK-bound; and full JEE Main + Advanced and NEET programs for Indian college-bound NRI students. All 1:1, with IIT / AIIMS / Ivy League alumni mentors."],
    ];

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${CANONICAL}#faq`,
        "mainEntity": faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    return (
        <div data-testid="online-tuition-worldwide">
            <SEO
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                keywords={SEO_KEYWORDS}
                path="/online-tuition-worldwide"
                canonicalPath="/online-tuition-worldwide"
                hreflangs={getInternationalHreflangs()}
                schemas={[organizationSchema, webPageSchema, serviceSchema, breadcrumbSchema, faqSchema]}
            />
            <AEOSchema
                isQA
                canonicalUrl={CANONICAL}
                primaryQuestion="Which countries does Anand Tutorials serve for 1:1 online tuition?"
                primaryAnswer={`Anand Tutorials delivers 1:1 live online tuition to students in ${countryCount}+ countries — including USA, UK, Canada, Australia, New Zealand, UAE, Saudi Arabia, Qatar, Kuwait, Oman, Bahrain, Singapore, Malaysia, Japan, South Korea, Germany, France, Netherlands, Ireland, Switzerland, Belgium, Italy, Spain, Norway, Sweden, Finland and Denmark. Every session is live 1:1 with Cambridge, Oxford, IIT or AIIMS alumni mentors — never batch — recorded for revision, at your child's preferred time-zone slot.`}
                keyFacts={[
                    `Serves students in ${countryCount}+ countries across North America, UK & Ireland, Europe, Middle East/GCC and Asia-Pacific.`,
                    "Curricula: IB MYP & DP, IGCSE Cambridge, AS/A-Level (Edexcel/CIE/AQA), AP, Common Core, GCSE, VCE, HSC, NCEA, Leaving Cert, plus CBSE & ICSE for NRI families.",
                    "Time-zone friendly — dedicated tutor shifts for every region (EST/PST, CET, JST/MYT/AEST).",
                    "Pay in your local currency — USD, GBP, EUR, AUD, CAD, SGD, AED, JPY, KRW, MYR, NOK, SEK, DKK, CHF and 10+ more via Wise, PayPal, Stripe or wire.",
                    "Free demo class before any payment; free tutor replacement within first 2 sessions.",
                    "Cambridge / Oxford / IIT / AIIMS educated mentors — less than 12% of applicants make it into the global network.",
                ]}
                aboutEntity="1:1 Live Online Tuition Worldwide"
            />

            <Breadcrumbs items={[
                { name: "1:1 Online Classes", to: "/online-classes" },
                { name: "Worldwide" },
            ]} />

            <PageHeader
                eyebrow={`🌍 Global · ${countryCount}+ Countries`}
                title="1:1 Live Online Tuition Worldwide"
                subtitle="From New York to Tokyo, London to Sydney, Dubai to Oslo — Anand Tutorials delivers Cambridge / Oxford / IIT / AIIMS educated 1:1 online tutors to students in 27+ countries. IB, IGCSE, A-Level, AP, Common Core, GCSE, VCE, HSC, CBSE, ICSE — pay in your currency, teach at your child's best after-school time."
            >
                <div className="flex flex-wrap gap-3">
                    <a
                        href={`tel:${ENQUIRY_PHONE_RAW}`}
                        data-testid="worldwide-call"
                        className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors"
                    >
                        <Phone className="w-4 h-4" /> Call Global Team
                    </a>
                    <Link
                        to="/register"
                        data-testid="worldwide-demo"
                        className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15"
                    >
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                        href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent("Hi Anand Tutorials, I would like 1:1 online tuition.")}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="worldwide-whatsapp"
                        className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#1ebe5a]"
                    >
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* Trust strip */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        { i: Globe2, n: `${countryCount}+`, l: "Countries served" },
                        { i: Users, n: "50,000+", l: "Students taught" },
                        { i: Star, n: `4.9 / 5`, l: `${totalReviews.toLocaleString()} parents worldwide` },
                        { i: Clock, n: "24 hrs", l: "Demo scheduled" },
                    ].map(({ i: I, n, l }) => (
                        <div key={l}>
                            <I className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{n}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Speakable intro (used by voice search / AI answer engines) */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-6">
                    <h2 className="speakable-headline font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">
                        Why families worldwide choose the best one on one online classes in the world
                    </h2>
                    <p className="speakable-intro text-slate-700 leading-relaxed text-base sm:text-lg">
                        Anand Tutorials is India&apos;s most trusted 1:1 live online tutoring platform for international students — widely rated as the best one on one online classes in the world by families across 27+ countries.
                        With 12+ years of experience and 50,000+ students taught, we deliver Cambridge, Oxford, IIT and AIIMS
                        alumni mentors — at 50-75% lower rates than local tutors, with
                        every session live 1:1 (never recorded lectures), recorded for revision, and backed by weekly Sunday
                        parent reports.
                    </p>
                </div>
            </section>

            {/* Region-grouped country grid — the main hub content */}
            <section className="py-16 bg-[#F8FAFC]" id="countries">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Choose Your Country
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                        Dedicated online-tuition pages for {countryCount}+ countries
                    </h2>
                    <p className="text-slate-600 mb-10 max-w-3xl">
                        Every country page is tuned to that country&apos;s curriculum (IB, IGCSE, AP, A-Level, VCE, HSC, NCEA,
                        Leaving Cert, etc.), time-zone-friendly scheduling and local-currency pricing. Pick your country to
                        book a free 1:1 demo.
                    </p>

                    <div className="space-y-10">
                        {REGIONS.map((region) => (
                            <div key={region.title}>
                                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                                    <span className="text-2xl">{region.icon}</span> {region.title}
                                </h3>
                                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {region.countries.map((slug) => {
                                        const c = COUNTRIES_DATA[slug];
                                        if (!c) return null;
                                        return (
                                            <Link
                                                key={slug}
                                                to={`/${c.urlSlug}`}
                                                data-testid={`worldwide-country-${slug}`}
                                                className="group bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md rounded-xl px-4 py-4 transition-all"
                                            >
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-2xl leading-none">{c.flag}</span>
                                                        <span className="font-heading font-semibold text-[#0F172A] text-sm sm:text-base">
                                                            {c.name}
                                                        </span>
                                                    </div>
                                                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    {c.currency} · {c.timezones.split("·")[0].trim()}
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Curricula pillar */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Global Curricula Covered
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                        Every major international curriculum — taught 1:1 live online
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            ["IB MYP & DP (HL / SL)", "TOK · EE · IA support · all subject groups"],
                            ["IGCSE Cambridge", "Core + Extended + Additional Mathematics · all boards"],
                            ["AS / A-Level", "Edexcel · CIE · AQA · OCR · WJEC"],
                            ["AP (Advanced Placement)", "AP Calc AB/BC · AP Physics 1/2/C · AP Chem · AP Bio · AP Stats · AP CS · AP English · AP History · AP Economics"],
                            ["Common Core (USA K-12)", "State-aligned support · SAT · ACT · AMC · AIME · USACO · Common App"],
                            ["GCSE (UK)", "All subjects, all UK exam boards · 11+ entrance exam preparation"],
                            ["VCE · HSC · QCE · WACE · SACE (Australia)", "Full state-board coverage · ATAR-targeted · NAPLAN · Selective / OC test prep"],
                            ["NCEA (New Zealand)", "Levels 1, 2 and 3 · all subjects"],
                            ["Leaving Certificate (Ireland)", "Junior Cycle · Leaving Cert Ordinary + Higher"],
                            ["Matura (Switzerland) · Abitur (Germany)", "Subject support in English or bilingual on request"],
                            ["SPM (Malaysia) · SEAB (Singapore)", "PSLE · GCE O-Level · A-Level · SPM"],
                            ["CBSE · ICSE (for NRI students)", "Class 1-12 · aligned with Indian schools · Hindi as second language"],
                        ].map(([title, desc]) => (
                            <div key={title} className="p-5 bg-[#F8FAFC] border border-slate-200 rounded-xl">
                                <div className="font-heading font-semibold text-[#1E3A8A] mb-1 text-sm sm:text-base">
                                    {title}
                                </div>
                                <div className="text-xs text-slate-600 leading-relaxed">{desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Why Families Worldwide Choose Us
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                        Premium teaching, time-zone friendly, pay in your currency.
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            [GraduationCap, "Cambridge / Oxford / IIT / AIIMS educated tutors", "Less than 12% of applicants make it into our global network. Every internationally-facing tutor is hand-picked, demo-tested and background-verified."],
                            [Video, "Live 1:1 — never recorded lectures", "One student, one tutor, one personalised plan. Two-way HD video on Zoom or Google Meet, digital whiteboard, screen-share, recorded for revision."],
                            [Globe2, "Time-zone friendly", "Dedicated tutor shifts for every region — from EST/PST for Americas to CET for Europe to MYT/JST/AEST for Asia-Pacific."],
                            [BadgeDollarSign, "Pay in your local currency", "USD, GBP, EUR, AUD, CAD, SGD, AED, JPY, MYR and 15+ more. Wise · PayPal · Stripe · international wire. No conversion penalties."],
                            [ShieldCheck, "Free tutor replacement", "If you're not satisfied after the first 2 sessions, we rematch the tutor at no cost — no questions asked."],
                            [Award, "Weekly Sunday parent reports", "What was covered, homework assigned, next week's plan — clear, written, every week."],
                        ].map(([I, t, d]) => (
                            <div key={t} className="p-6 bg-white rounded-2xl border border-slate-200">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-3">
                                    <I className="w-6 h-6" />
                                </div>
                                <div className="font-heading font-semibold text-[#0F172A] mb-1">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ + Registration */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                            Worldwide FAQs
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                            Frequently asked questions from parents worldwide
                        </h2>
                        <div className="space-y-3">
                            {faqs.map(([q, a]) => (
                                <details
                                    key={q}
                                    className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden"
                                >
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between">
                                        {q}
                                        <span className="text-[#3B82F6] text-2xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
                                </details>
                            ))}
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3 text-sm">
                            <Link to="/online-classes" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                India · 1:1 Online Classes <ArrowRight className="w-4 h-4" />
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/foreign-students" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                NRI / Foreign Students <ArrowRight className="w-4 h-4" />
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/courses" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                All courses <ArrowRight className="w-4 h-4" />
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/blog" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                Blog <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-2" id="book-demo">
                        <RegistrationForm title="Book Free Online Demo — Worldwide" />
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                        Wherever your child studies — we teach.
                    </h2>
                    <p className="text-blue-100 max-w-2xl mx-auto mb-6">
                        Free 1:1 live online demo with a Cambridge / Oxford / IIT / AIIMS educated tutor at your preferred
                        time zone. No payment, no commitment.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a
                            href={`tel:${ENQUIRY_PHONE_RAW}`}
                            className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400"
                        >
                            <Phone className="w-4 h-4" /> Call Global Team
                        </a>
                        <Link
                            to="/register"
                            className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] font-semibold px-6 py-3 rounded-lg hover:bg-slate-50"
                        >
                            Book Free Demo
                        </Link>
                        <a
                            href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent("Hi Anand Tutorials, I would like to enquire about 1:1 online tuition.")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#1ebe5a]"
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
